import {
  AI_HOLIDAY_THEMES,
  AI_SEASON_THEMES,
  type AiTheme,
} from './ai-theme.model';

/** Local midnight of a calendar date; `month` is 1–12. */
const day = (year: number, month: number, date: number) =>
  new Date(year, month - 1, date);

const addDays = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** Easter Sunday in the Gregorian calendar (the anonymous computus). */
export const easterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const date = ((h + l - 7 * m + 114) % 31) + 1;
  return day(year, month, date);
};

/** Swedish Midsummer Eve: the Friday between 19 and 25 June. */
export const midsummerEve = (year: number): Date => {
  const first = day(year, 6, 19);
  return addDays(first, (5 - first.getDay() + 7) % 7);
};

/** True when `date` falls on a day from `from` to `to`, both included. */
const within = (date: Date, from: Date, to: Date) => {
  const today = day(date.getFullYear(), date.getMonth() + 1, date.getDate());
  return today.getTime() >= from.getTime() && today.getTime() <= to.getTime();
};

/**
 * The theme for a date: a holiday when one is on, otherwise the season
 * (meteorological, northern hemisphere).
 */
export const aiThemeForDate = (date: Date): AiTheme => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const holidays: [Date, Date, AiTheme][] = [
    [day(year, 1, 1), day(year, 1, 1), AI_HOLIDAY_THEMES.newYear],
    [
      addDays(easterSunday(year), -3),
      addDays(easterSunday(year), 1),
      AI_HOLIDAY_THEMES.easter,
    ],
    [
      midsummerEve(year),
      addDays(midsummerEve(year), 2),
      AI_HOLIDAY_THEMES.midsummer,
    ],
    [day(year, 10, 24), day(year, 10, 31), AI_HOLIDAY_THEMES.halloween],
    [day(year, 12, 13), day(year, 12, 13), AI_HOLIDAY_THEMES.lucia],
    [day(year, 12, 20), day(year, 12, 26), AI_HOLIDAY_THEMES.christmas],
    [day(year, 12, 31), day(year, 12, 31), AI_HOLIDAY_THEMES.newYear],
  ];
  const holiday = holidays.find(([from, to]) => within(date, from, to));
  if (holiday) return holiday[2];
  if (month === 12 || month <= 2) return AI_SEASON_THEMES.winter;
  if (month <= 5) return AI_SEASON_THEMES.spring;
  if (month <= 8) return AI_SEASON_THEMES.summer;
  return AI_SEASON_THEMES.autumn;
};
